import React, { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';

import HpLazyImageWrapper from '../HpLazyImageWrapper/HpLazyImageWrapper';
import ResponsiveImage from '../ResponsiveImage';
import HpRespHeroBannerText from '../HpRespHeroBannerText/HpRespHeroBannerText';
import classnames from 'classnames';

import { HpRespSharedHeroBanner_topItem as TopItem } from './__generated__/HpRespSharedHeroBanner_topItem.graphql';
import { HpRespSharedHeroBanner_promoItem as PromoItem } from './__generated__/HpRespSharedHeroBanner_promoItem.graphql';

import styles from './HpRespSharedHeroBanner.scss';

type Props = {
    isVisible: boolean;
    onClick: () => void;
    useLazyLoadImages?: boolean;
} & (
    | {
          topItem: TopItem;
          promoItem?: never;
      }
    | {
          topItem?: never;
          promoItem: PromoItem;
      }
);

const HpRespSharedHeroBanner: FC<Props> = ({
    isVisible,
    onClick,
    useLazyLoadImages,
    topItem,
    promoItem,
}) => {
    const {
        cmsId,
        imageUrl,
        mobileImageUrl,
        imageHeight = 0,
        imageWidth = 0,
        mobileImageHeight = 0,
        mobileImageWidth = 0,
        linkData = null,
        cta = null,
        heroBannerSettings = null,
    } = topItem || promoItem || {};

    const { backgroundColor, bannerImageWidth, contentPosition } = heroBannerSettings || {};
    const isFullWidth = bannerImageWidth === 'full';
    const isReverse = contentPosition === 'right';
    return (
        <div
            className={classnames(styles.contentContainer, {
                [styles.fullWidth]: isFullWidth,
                [styles.reverse]: isReverse,
                [styles.visible]: isVisible,
                [styles.border]: !backgroundColor && !isFullWidth,
            })}
            style={{
                backgroundColor: backgroundColor || undefined,
            }}
        >
            <HpRespHeroBannerText
                onClick={onClick}
                className={styles.textContainer}
                heroBannerSettings={heroBannerSettings}
                linkData={linkData}
                cta={cta}
            />
            <HpLazyImageWrapper
                className={classnames(styles.imageContainer, {
                    [styles.fullWidth]: isFullWidth,
                })}
                useLazyLoad={useLazyLoadImages}
                imageHeight={mobileImageHeight || imageHeight}
                imageWidth={mobileImageWidth || imageWidth}
            >
                <ResponsiveImage
                    ariaHidden
                    className={classnames(styles.image, { [styles.fullWidth]: isFullWidth })}
                    src={imageUrl || mobileImageUrl || ''}
                />
            </HpLazyImageWrapper>
            {/* eslint-disable-next-line react/forbid-elements, jsx-a11y/anchor-has-content */}
            <a
                // this link is redundant with the cta in HpRespHeroBannerText. do not add
                // label/children, hide from a11y-tree, and prevent keyboard focus.
                aria-hidden
                key={cmsId}
                tabIndex={-1}
                href={linkData?.path || undefined}
                className={styles.overlayLink}
                onClick={onClick}
            />
        </div>
    );
};

export const HpRespTopHeroBannerItem = createFragmentContainer(HpRespSharedHeroBanner, {
    topItem: graphql`
        fragment HpRespSharedHeroBanner_topItem on TopHeroBannerItem {
            cmsId
            ... on TopHeroBannerItem @skip(if: $isMobile) {
                imageUrl
                imageHeight
                imageWidth
            }
            ... on TopHeroBannerItem @include(if: $isMobile) {
                mobileImageHeight
                mobileImageWidth
                mobileImageUrl
            }
            linkData {
                path
                ...HpRespHeroBannerText_linkData
            }
            heroBannerSettings {
                ...HpRespHeroBannerText_heroBannerSettings
                contentPosition
                backgroundColor
                bannerImageWidth
            }
            cta
            isFullBleedImage
            format
        }
    `,
});

export const HpRespPromoHeroItem = createFragmentContainer(HpRespSharedHeroBanner, {
    promoItem: graphql`
        fragment HpRespSharedHeroBanner_promoItem on PromoItem {
            cmsId
            ... on PromoItem @skip(if: $isMobile) {
                imageUrl
                imageHeight
                imageWidth
            }
            ... on PromoItem @include(if: $isMobile) {
                mobileImageHeight
                mobileImageWidth
                mobileImageUrl
            }
            linkData {
                path
                ...HpRespHeroBannerText_linkData
            }
            heroBannerSettings {
                ...HpRespHeroBannerText_heroBannerSettings
                contentPosition
                backgroundColor
                bannerImageWidth
            }
            cta
            isFullBleedImage
            format
        }
    `,
});
